/**
 * Utility functions for handling retries with exponential backoff
 * Used for S3 uploads and API confirmations
 */
import * as Sentry from '@sentry/react';

/**
 * Upload error types for categorization
 */
export const UploadErrorType = {
  S3_UPLOAD: 's3_upload',
  S3_TIMEOUT: 's3_timeout',
  CONFIRMATION: 'confirmation',
  NETWORK: 'network',
  PRESIGNED_URL_EXPIRED: 'presigned_url_expired',
  UNKNOWN: 'unknown',
};

/**
 * Categorize an error based on its characteristics
 * @param {Error|string} error - The error to categorize
 * @param {number} [statusCode] - Optional HTTP status code
 * @returns {string} Error type from UploadErrorType
 */
export function categorizeError(error, statusCode) {
  const errorMessage = typeof error === 'string' ? error : error?.message || '';
  const lowerMessage = errorMessage.toLowerCase();

  if (statusCode === 403 && (lowerMessage.includes('expired') || lowerMessage.includes('accessdenied'))) {
    return UploadErrorType.PRESIGNED_URL_EXPIRED;
  }

  if (lowerMessage.includes('timeout') || lowerMessage.includes('tardó más de')) {
    return UploadErrorType.S3_TIMEOUT;
  }

  if (
    lowerMessage.includes('network') ||
    lowerMessage.includes('fetch') ||
    lowerMessage.includes('connection') ||
    lowerMessage.includes('econnreset') ||
    lowerMessage.includes('econnrefused')
  ) {
    return UploadErrorType.NETWORK;
  }

  if (statusCode && statusCode >= 400) {
    return UploadErrorType.S3_UPLOAD;
  }

  return UploadErrorType.UNKNOWN;
}

/**
 * Report an upload error to Sentry with structured context
 * @param {Object} params - Error parameters
 * @param {string} params.operation - Operation that failed (e.g., 's3_upload', 'confirmation')
 * @param {string} params.fileName - Name of the file being uploaded
 * @param {number} params.fileSize - Size of the file in bytes
 * @param {string} params.contentType - MIME type of the file
 * @param {number} params.attempt - Current attempt number
 * @param {number} params.maxRetries - Maximum retry attempts
 * @param {Error|string} params.error - The error that occurred
 * @param {number} [params.statusCode] - HTTP status code if applicable
 * @param {string} [params.attachmentId] - ID of the attachment
 * @param {string} [params.documentId] - ID of the document
 * @param {string} [params.discrepancyId] - ID of the discrepancy
 * @param {boolean} [params.isFinalAttempt] - Whether this is the last retry attempt
 */
export function reportUploadError({
  operation,
  fileName,
  fileSize,
  contentType,
  attempt,
  maxRetries,
  error,
  statusCode,
  attachmentId,
  documentId,
  discrepancyId,
  isFinalAttempt = false,
}) {
  const errorMessage = typeof error === 'string' ? error : error?.message || 'Unknown error';
  const errorType = categorizeError(error, statusCode);
  const severity = isFinalAttempt ? 'error' : 'warning';

  // Attached to the captured event below instead of `Sentry.setContext`, which
  // writes to the global scope and leaks these details into later, unrelated
  // events of the same session.
  const uploadDetails = {
    operation,
    fileName,
    fileSize,
    fileSizeFormatted: formatFileSize(fileSize),
    contentType,
    attempt,
    maxRetries,
    isFinalAttempt,
    errorType,
    statusCode,
    attachmentId,
    documentId,
    discrepancyId,
    timestamp: new Date().toISOString(),
    userAgent: navigator?.userAgent,
    online: navigator?.onLine,
  };

  // Add breadcrumb for tracking the upload flow
  Sentry.addBreadcrumb({
    category: 'upload',
    message: `${operation} attempt ${attempt}/${maxRetries} failed`,
    level: severity,
    data: {
      fileName,
      errorType,
      statusCode,
      errorMessage: errorMessage.substring(0, 200), // Truncate long messages
    },
  });

  // Only capture as exception on final attempt or non-recoverable errors
  if (isFinalAttempt || !isRecoverableError(error, statusCode)) {
    Sentry.captureException(
      error instanceof Error ? error : new Error(errorMessage),
      {
        level: severity,
        contexts: { upload_details: uploadDetails },
        tags: {
          operation,
          errorType,
          isFinalAttempt: String(isFinalAttempt),
          statusCode: statusCode ? String(statusCode) : undefined,
        },
        fingerprint: [operation, errorType, String(statusCode || 'no_status')],
      }
    );

    console.error(`[SENTRY] Reported ${operation} error:`, {
      errorType,
      fileName,
      attempt,
      isFinalAttempt,
      statusCode,
    });
  }
  // A recoverable failure that is about to be retried is recorded by the
  // breadcrumb above and nothing else. Capturing it opened an unresolved Sentry
  // issue for ordinary retry traffic (DISCREPANCIES-FRONTEND-3S); if the retries
  // run out, the `captureException` branch above still reports it.
}

/**
 * Report a successful upload after retries (for tracking recovery)
 * @param {Object} params - Success parameters
 * @param {string} params.operation - Operation that succeeded
 * @param {string} params.fileName - Name of the file
 * @param {number} params.attempt - Attempt number that succeeded
 * @param {number} params.totalTime - Total time taken in ms
 */
export function reportUploadSuccess({ operation, fileName, attempt, totalTime }) {
  if (attempt > 1) {
    // Only report if it took retries to succeed. Recorded as a breadcrumb only:
    // a recovered upload is not an issue (DISCREPANCIES-FRONTEND-3S sibling).
    Sentry.addBreadcrumb({
      category: 'upload',
      message: `${operation} succeeded after ${attempt} attempts`,
      level: 'info',
      data: {
        fileName,
        attempt,
        totalTimeMs: totalTime,
      },
    });
  }
}

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Default configuration for retry logic
 */
export const DEFAULT_RETRY_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 30000,
  timeoutMs: 120000, // 2 minutes
  backoffMultiplier: 2,
};

/**
 * Errors that are considered recoverable and worth retrying
 */
const RECOVERABLE_ERROR_PATTERNS = [
  'network',
  'timeout',
  'ECONNRESET',
  'ECONNREFUSED',
  'ETIMEDOUT',
  'ENOTFOUND',
  'fetch failed',
  'Failed to fetch',
  'Network request failed',
  'conexión',
  'connection',
  'socket hang up',
  '502',
  '503',
  '504',
  'Service Unavailable',
  'Gateway Timeout',
  'Bad Gateway',
];

/**
 * HTTP status codes that are recoverable
 */
const RECOVERABLE_STATUS_CODES = [
  408, // Request Timeout
  429, // Too Many Requests
  500, // Internal Server Error
  502, // Bad Gateway
  503, // Service Unavailable
  504, // Gateway Timeout
];

/**
 * Determines if an error is recoverable and worth retrying
 * @param {Error|string} error - The error to check
 * @param {number} [statusCode] - Optional HTTP status code
 * @returns {boolean}
 */
export function isRecoverableError(error, statusCode) {
  // Check status code first
  if (statusCode && RECOVERABLE_STATUS_CODES.includes(statusCode)) {
    return true;
  }

  // Check error message patterns
  const errorMessage = typeof error === 'string' ? error : error?.message || '';
  return RECOVERABLE_ERROR_PATTERNS.some(pattern =>
    errorMessage.toLowerCase().includes(pattern.toLowerCase())
  );
}

/**
 * Calculate delay with exponential backoff and jitter
 * @param {number} attempt - Current attempt number (0-indexed)
 * @param {Object} config - Retry configuration
 * @returns {number} Delay in milliseconds
 */
export function calculateBackoffDelay(attempt, config = DEFAULT_RETRY_CONFIG) {
  const { baseDelayMs, maxDelayMs, backoffMultiplier } = config;

  // Exponential backoff: baseDelay * (multiplier ^ attempt)
  const exponentialDelay = baseDelayMs * Math.pow(backoffMultiplier, attempt);

  // Add jitter (±25% randomization) to prevent thundering herd
  const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);

  // Clamp to max delay
  return Math.min(exponentialDelay + jitter, maxDelayMs);
}

/**
 * Sleep for a specified duration
 * @param {number} ms - Duration in milliseconds
 * @returns {Promise<void>}
 */
export function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Wraps a fetch call with timeout
 * @param {string} url - URL to fetch
 * @param {RequestInit} options - Fetch options
 * @param {number} timeoutMs - Timeout in milliseconds
 * @returns {Promise<Response>}
 */
export async function fetchWithTimeout(url, options, timeoutMs = DEFAULT_RETRY_CONFIG.timeoutMs) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error(`Upload timeout - la conexión tardó más de ${Math.round(timeoutMs / 1000)} segundos`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Upload a file to S3 with retry logic
 * @param {string} presignedUrl - Pre-signed S3 URL
 * @param {File} file - File to upload
 * @param {string} contentType - MIME type
 * @param {Object} [config] - Retry configuration
 * @param {Function} [onProgress] - Progress callback (attempt, maxRetries)
 * @returns {Promise<{success: boolean, error?: string, statusCode?: number}>}
 */
export async function uploadToS3WithRetry(
  presignedUrl,
  file,
  contentType,
  config = DEFAULT_RETRY_CONFIG,
  onProgress = null
) {
  const { maxRetries, timeoutMs } = config;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      if (onProgress) {
        onProgress(attempt + 1, maxRetries);
      }

      console.log(`[S3_UPLOAD] Attempt ${attempt + 1}/${maxRetries} - Uploading ${file.name}`);

      const response = await fetchWithTimeout(
        decodeURI(presignedUrl),
        {
          method: 'PUT',
          headers: {
            'Content-Type': contentType,
          },
          body: file,
        },
        timeoutMs
      );

      if (response.ok) {
        console.log(`[S3_UPLOAD] Success: ${file.name}`);
        return { success: true };
      }

      const statusCode = response.status;
      const errorText = await response.text().catch(() => 'Unknown error');

      console.error(`[S3_UPLOAD] Attempt ${attempt + 1} failed: HTTP ${statusCode}`, errorText);

      // Check if error is recoverable
      if (!isRecoverableError(errorText, statusCode) && attempt < maxRetries - 1) {
        // Non-recoverable error, but let's try once more just in case
        console.log(`[S3_UPLOAD] Error might not be recoverable, but will retry once more`);
      }

      if (attempt < maxRetries - 1) {
        const delayMs = calculateBackoffDelay(attempt, config);
        console.log(`[S3_UPLOAD] Waiting ${Math.round(delayMs)}ms before retry...`);
        await sleep(delayMs);
      } else {
        return {
          success: false,
          error: `Error HTTP ${statusCode}: ${errorText}`,
          statusCode,
        };
      }
    } catch (error) {
      console.error(`[S3_UPLOAD] Attempt ${attempt + 1} error:`, error.message);

      if (attempt < maxRetries - 1) {
        const isRecoverable = isRecoverableError(error);

        if (!isRecoverable) {
          console.warn(`[S3_UPLOAD] Error might not be recoverable: ${error.message}`);
        }

        const delayMs = calculateBackoffDelay(attempt, config);
        console.log(`[S3_UPLOAD] Waiting ${Math.round(delayMs)}ms before retry...`);
        await sleep(delayMs);
      } else {
        return {
          success: false,
          error: error.message || 'Error desconocido al subir archivo',
        };
      }
    }
  }

  return {
    success: false,
    error: `Máximo de ${maxRetries} reintentos alcanzado`,
  };
}

/**
 * Execute an API call with retry logic (for confirmations, etc.)
 * @param {Function} apiCall - Async function that makes the API call
 * @param {Object} [config] - Retry configuration
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export async function executeWithRetry(apiCall, config = DEFAULT_RETRY_CONFIG) {
  const { maxRetries } = config;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      console.log(`[API_RETRY] Attempt ${attempt + 1}/${maxRetries}`);

      const result = await apiCall();

      // Check if the result indicates an error (API-specific)
      if (result && !result.error) {
        console.log(`[API_RETRY] Success on attempt ${attempt + 1}`);
        return { success: true, data: result };
      }

      // API returned an error response
      const errorMsg = result?.errorMsg || result?.message || 'Error en la respuesta del servidor';
      console.error(`[API_RETRY] Attempt ${attempt + 1} API error:`, errorMsg);

      if (attempt < maxRetries - 1) {
        const delayMs = calculateBackoffDelay(attempt, config);
        console.log(`[API_RETRY] Waiting ${Math.round(delayMs)}ms before retry...`);
        await sleep(delayMs);
      } else {
        return {
          success: false,
          error: errorMsg,
          data: result,
        };
      }
    } catch (error) {
      console.error(`[API_RETRY] Attempt ${attempt + 1} exception:`, error.message);

      if (attempt < maxRetries - 1 && isRecoverableError(error)) {
        const delayMs = calculateBackoffDelay(attempt, config);
        console.log(`[API_RETRY] Waiting ${Math.round(delayMs)}ms before retry...`);
        await sleep(delayMs);
      } else if (attempt >= maxRetries - 1) {
        return {
          success: false,
          error: error.message || 'Error desconocido',
        };
      } else {
        // Non-recoverable error
        return {
          success: false,
          error: error.message || 'Error no recuperable',
        };
      }
    }
  }

  return {
    success: false,
    error: `Máximo de ${maxRetries} reintentos alcanzado`,
  };
}

/**
 * Redux-saga compatible generator for uploading to S3 with retry
 * @param {string} presignedUrl - Pre-signed S3 URL
 * @param {File} file - File to upload
 * @param {string} contentType - MIME type
 * @param {Object} [config] - Retry configuration
 * @yields {any}
 * @returns {{success: boolean, error?: string}}
 */
export function* uploadToS3WithRetrySaga(presignedUrl, file, contentType, config = DEFAULT_RETRY_CONFIG) {
  const { maxRetries, timeoutMs } = config;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      console.log(`[S3_UPLOAD] Attempt ${attempt + 1}/${maxRetries} - Uploading ${file.name}`);

      // Create the upload promise with timeout
      const uploadPromise = Promise.race([
        fetch(decodeURI(presignedUrl), {
          method: 'PUT',
          headers: {
            'Content-Type': contentType,
          },
          body: file,
        }),
        new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error(`Upload timeout - la conexión tardó más de ${Math.round(timeoutMs / 1000)} segundos`)),
            timeoutMs
          )
        ),
      ]);

      const response = yield uploadPromise;

      if (response.ok) {
        console.log(`[S3_UPLOAD] Success: ${file.name}`);
        return { success: true };
      }

      const statusCode = response.status;
      let errorText = '';
      try {
        errorText = yield response.text();
      } catch (e) {
        errorText = 'Unknown error';
      }

      console.error(`[S3_UPLOAD] Attempt ${attempt + 1} failed: HTTP ${statusCode}`, errorText);

      if (attempt < maxRetries - 1) {
        const delayMs = calculateBackoffDelay(attempt, config);
        console.log(`[S3_UPLOAD] Waiting ${Math.round(delayMs)}ms before retry...`);
        yield sleep(delayMs);
      } else {
        return {
          success: false,
          error: `Error HTTP ${statusCode}: ${errorText}`,
          statusCode,
        };
      }
    } catch (error) {
      console.error(`[S3_UPLOAD] Attempt ${attempt + 1} error:`, error.message);

      if (attempt < maxRetries - 1) {
        const delayMs = calculateBackoffDelay(attempt, config);
        console.log(`[S3_UPLOAD] Waiting ${Math.round(delayMs)}ms before retry...`);
        yield sleep(delayMs);
      } else {
        return {
          success: false,
          error: error.message || 'Error desconocido al subir archivo',
        };
      }
    }
  }

  return {
    success: false,
    error: `Máximo de ${maxRetries} reintentos alcanzado`,
  };
}

export default {
  DEFAULT_RETRY_CONFIG,
  UploadErrorType,
  categorizeError,
  reportUploadError,
  reportUploadSuccess,
  isRecoverableError,
  calculateBackoffDelay,
  sleep,
  fetchWithTimeout,
  uploadToS3WithRetry,
  executeWithRetry,
  uploadToS3WithRetrySaga,
};
